import React, { useEffect, useRef } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledAboutSection = styled.section`
  max-width: 900px;

  .inner {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 50px;

    @media (max-width: 768px) {
      display: block;
    }
  }
`;
const StyledText = styled.div`
  ul.skills-list {
    display: grid;
    grid-template-columns: repeat(3, minmax(140px, 200px));
    grid-gap: 0 10px;
    padding: 0;
    margin: 20px 0 0 0;
    overflow: hidden;
    list-style: none;

    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
      font-family: var(--font-mono);
      font-size: var(--fz-xs);

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--green);
        font-size: var(--fz-sm);
        line-height: 12px;
      }
    }
  }
`;
const StyledPic = styled.div`
  position: relative;
  max-width: 300px;

  @media (max-width: 768px) {
    margin: 50px auto 0;
    width: 70%;
  }

  .wrapper {
    ${({ theme }) => theme.mixins.boxShadow};
    display: block;
    position: relative;
    width: 100%;
    border-radius: var(--border-radius);
    background-color: var(--green);

    &:hover,
    &:focus {
      outline: 0;
      transform: translate(-4px, -4px);

      &:after {
        transform: translate(8px, 8px);
      }

      .img {
        filter: none;
        mix-blend-mode: normal;
      }
    }

    .img {
      position: relative;
      border-radius: var(--border-radius);
      mix-blend-mode: multiply;
      filter: grayscale(100%) contrast(1);
      transition: var(--transition);
    }

    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
      transition: var(--transition);
    }

    &:before {
      top: 0;
      left: 0;
      background-color: var(--navy);
      mix-blend-mode: screen;
    }

    &:after {
      border: 2px solid var(--green);
      top: 14px;
      left: 14px;
      z-index: -1;
    }
  }
`;

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  const skills = [
    'JavaScript (ES6+)',
    'TypeScript',
    'ReactJS',
    'NextJS',
    'JQuery',
    'Webflow',
    'BubbleIO',
    'WordPress',
  ];

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">About Me</h2>

      <div className="inner">
        <StyledText>
          <div>
            <p>
              I'm Bilal, a passionate creator in the digital realm, shaping vibrant experiences on
              the web. My journey into the exciting world of web development commenced in 2019 when
              I first delved into crafting custom WordPress websites. It's amazing how tinkering
              with a reblog button taught me the magic of HTML &amp; CSS!
            </p>
            <p>
              Today, I've had the incredible opportunity to contribute my skills at various exciting
              domains—ranging from the dynamic landscape of{' '}
              <a href="https://techupsolutions.net/" target="_blank">
                advertising agencies
              </a>{' '}
              to innovative{' '}
              <a href="https://web.facebook.com/fiestadigitalsolutions" target="_blank">
                start-ups
              </a>
              , the giants at{' '}
              <a href="https://www.dynamologic.com/" target="_blank">
                corporations
              </a>
              , and the creative environment of{' '}
              <a href="https://www.cloudmeshsolutions.com/" target="_blank">
                student-led design studios
              </a>
              . At present, I'm dedicated to crafting accessible, inclusive digital products and
              experiences at{' '}
              <a href="https://www.upwork.com/freelancers/bilaltufailkhan" target="_blank">
                Upwork
              </a>
              , catering to diverse clientele.
            </p>
            {/* <p>
              Exciting news! I've recently introduced a comprehensive{' '}
              <a href="https://www.newline.co/courses/build-a-spotify-connected-app">course</a> that
              unveils the secrets of building a web app using the Spotify API with the prowess of
              Node &amp; React—your gateway to creating extraordinary web applications.
            </p> */}
            <p>Let me share a glimpse of the technologies I've been immersed in lately:</p>
          </div>

          <ul className="skills-list">
            {skills && skills.map((skill, i) => <li key={i}>{skill}</li>)}
          </ul>
        </StyledText>

        <StyledPic>
          <div className="wrapper">
            <StaticImage
              className="img"
              src="../../images/bilaltufaildev.jpeg"
              width={500}
              quality={95}
              formats={['AUTO', 'WEBP', 'AVIF', 'JPEG']}
              alt="Headshot"
            />
          </div>
        </StyledPic>
      </div>
    </StyledAboutSection>
  );
};

export default About;
